import { Badge, Button, Card, CardContent, CardHeader, CardTitle, DataGrid, type DataGridLeafColumn } from '@ibsys/kit';
import { ArrowLeftIcon, ArrowLeftRightIcon, ArrowRightIcon, ListFilterIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { getKmEntityTitle, useKmEntityGraph, useKmRelationTypes } from '@/entities/ontology';
import { dataGridHeightByRows } from '@/shared/lib';
import {
  applyKmEntitiesFilter,
  buildKmEntitiesListLocation,
  buildKmEntityLocation,
} from '../../../model/entitiesFilter';

type Props = {
  /** Идентификатор открытой сущности */
  entityId: number;
};

/** Строка таблицы связей относительно открытой сущности */
type RelationRow = {
  id: number;
  /** Название типа связи; вне справочника — его код */
  typeName: string;
  /** Связь направленная: у взаимной стороны равноправны */
  isDirected: boolean;
  /** Связь исходит из открытой сущности */
  isOutgoing: boolean;
  /** Идентификатор второй стороны */
  relatedId: number;
  /** Название второй стороны — по тому же правилу, что и в списке */
  relatedTitle: string;
};

/**
 * Секция «Связи» — только для чтения. Связи и вторые стороны берутся из одного ответа графа,
 * поэтому карточки вторых сторон отдельно не запрашиваются. Без связей секция не показывается.
 */
export const KmEntityRelationsSection: FC<Props> = ({ entityId }) => {
  const { t } = useTranslation(['kmEntities', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: graph } = useKmEntityGraph(entityId, { depth: 1 });
  const { data: relationTypesData } = useKmRelationTypes({ limit: 1000 });

  const rows = useMemo<RelationRow[]>(() => {
    const relationTypes = new Map((relationTypesData?.items ?? []).map((type) => [type.code, type]));
    const entities = new Map((graph?.entities ?? []).map((entity) => [entity.id, entity]));

    return (graph?.relations ?? [])
      .filter((relation) => !relation.isDeleted)
      .map<RelationRow>((relation) => {
        const isOutgoing = relation.fromEntityId === entityId;
        const relatedId = isOutgoing ? relation.toEntityId : relation.fromEntityId;
        const related = entities.get(relatedId);
        const relationType = relationTypes.get(relation.relationTypeCode);

        return {
          id: relation.id,
          typeName: relationType?.name ?? relation.relationTypeCode,
          isDirected: relationType?.isDirected ?? true,
          isOutgoing,
          relatedId,
          relatedTitle: related ? getKmEntityTitle(related) : `#${relatedId}`,
        };
      });
  }, [graph, relationTypesData, entityId]);

  const columns = useMemo<DataGridLeafColumn<RelationRow>[]>(
    () => [
      {
        id: 'type',
        header: t('kmEntities:relationType'),
        size: 220,
        cell: ({ row }) => <span className="text-sm">{row.typeName}</span>,
      },
      {
        id: 'direction',
        header: t('kmEntities:relationDirection'),
        size: 180,
        cell: ({ row }) => {
          const { isDirected, isOutgoing } = row;
          if (!isDirected) {
            return (
              <Badge variant="secondary" outline className="gap-1">
                <ArrowLeftRightIcon className="size-3" />
                {t('kmEntities:relationMutual')}
              </Badge>
            );
          }
          return (
            <Badge variant="secondary" outline className="gap-1">
              {isOutgoing ? <ArrowRightIcon className="size-3" /> : <ArrowLeftIcon className="size-3" />}
              {isOutgoing ? t('kmEntities:relationOutgoing') : t('kmEntities:relationIncoming')}
            </Badge>
          );
        },
      },
      {
        id: 'related',
        header: t('kmEntities:relationRelated'),
        size: 420,
        // Переход по связи сохраняет отбор: возврат к списку не теряет фильтры и поиск
        cell: ({ row }) => (
          <NavLink
            to={buildKmEntityLocation(row.relatedId, searchParams)}
            className="text-sm text-primary hover:underline"
          >
            {row.relatedTitle}
          </NavLink>
        ),
      },
    ],
    [t, searchParams],
  );

  // Отбор списка по открытой сущности: прежний выбор связанных сущностей она заменяет,
  // прочие условия отбора сохраняются, а карточка закрывается — сама сущность в выдачу не входит
  const handleShowRelated = () => {
    navigate(buildKmEntitiesListLocation(applyKmEntitiesFilter(searchParams, 'related', String(entityId))));
  };

  if (!rows.length) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base">{t('kmEntities:relationsSectionTitle')}</CardTitle>
        <Button
          variant="outline"
          size="sm"
          className="self-end sm:self-auto"
          icon={<ListFilterIcon />}
          onClick={handleShowRelated}
        >
          {t('kmEntities:showRelatedEntities')}
        </Button>
      </CardHeader>
      {/* Грид держит ширину своих колонок: без ограничения он распирал бы карточку по ширине */}
      <CardContent className="min-w-0 overflow-hidden p-0">
        <DataGrid
          className="w-full"
          columns={columns}
          dataSource={{ mode: 'client', data: rows }}
          getRowId={(row) => String(row.id)}
          height={dataGridHeightByRows(rows.length)}
          ariaLabel={t('kmEntities:relationsSectionTitle')}
        />
      </CardContent>
    </Card>
  );
};
